/**
 * Redacción editable de las 2 cartas de Materialidad (por empresa).
 * Solo texto narrativo; datos de despacho/cliente/proveedor son placeholders fijos.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  fetchMaterialidadConfig,
  saveMaterialidadConfig,
} from "@/modules/tramites/materialidad/materialidadApi";

const LETTER_FIELDS = [
  {
    key: "carta_cliente_intro",
    letter: "cliente",
    label: "Cuerpo — carta despacho → cliente",
    hint: "Párrafo después de «Estimado cliente:». Use {periodo} si desea.",
  },
  {
    key: "carta_cliente_providers_lead",
    letter: "cliente",
    label: "Introducción a la tabla de proveedores",
    hint: "Texto previo al listado de proveedores (montos y riesgo no se editan).",
  },
  {
    key: "carta_cliente_legal_note",
    letter: "cliente",
    label: "Nota aclaratoria / legal",
    hint: "Pie de la carta resumen al cliente.",
  },
  {
    key: "carta_proveedor_intro",
    letter: "proveedor",
    label: "Cuerpo — carta cliente → proveedor",
    hint: "Tras «Estimado proveedor:». Placeholders: {periodo}, {cliente}, {proveedor}.",
  },
  {
    key: "carta_proveedor_footer",
    letter: "proveedor",
    label: "Pie / nota de la carta al proveedor",
    hint: "Nota final (no modifica firma ni RFC).",
  },
];

function effectiveLetterValue(config, defaults, key) {
  const raw = String(config?.[key] ?? "").trim();
  if (raw) return raw;
  return String(defaults?.[key] || "");
}

export default function MaterialidadCartasRedaccionPanel({ clientId, clientName }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [baseConfig, setBaseConfig] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [placeholders, setPlaceholders] = useState([]);
  const [note, setNote] = useState("");
  const [draft, setDraft] = useState({});

  const load = useCallback(async () => {
    if (!clientId) {
      setBaseConfig(null);
      setDraft({});
      return;
    }
    setLoading(true);
    try {
      const data = await fetchMaterialidadConfig(clientId);
      const cfg = data.config || {};
      const tpl = data.catalog?.letter_templates || {};
      const defs = tpl.defaults || {};
      setBaseConfig(cfg);
      setDefaults(defs);
      setPlaceholders(tpl.placeholders || []);
      setNote(tpl.note || "");
      const next = {};
      LETTER_FIELDS.forEach(({ key }) => {
        next[key] = effectiveLetterValue(cfg, defs, key);
      });
      setDraft(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar la redacción de cartas.");
      setBaseConfig(null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const placeholderChips = useMemo(
    () =>
      (placeholders.length
        ? placeholders
        : [
            { id: "cliente", label: "Nombre del cliente" },
            { id: "periodo", label: "Periodo" },
            { id: "proveedor", label: "Proveedor" },
            { id: "despacho", label: "Despacho" },
          ]
      ).map((p) => `{${p.id}}`),
    [placeholders],
  );

  const patchDraft = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const restoreDefaults = () => {
    const next = {};
    LETTER_FIELDS.forEach(({ key }) => {
      next[key] = String(defaults[key] || "");
    });
    setDraft(next);
    toast.message("Redacción restaurada a la plantilla por defecto (guarde para aplicar).");
  };

  const handleSave = async () => {
    if (!clientId || !baseConfig) return;
    setSaving(true);
    try {
      const payload = {
        ...baseConfig,
        carta_cliente_intro: String(draft.carta_cliente_intro || "").trim(),
        carta_cliente_providers_lead: String(draft.carta_cliente_providers_lead || "").trim(),
        carta_cliente_legal_note: String(draft.carta_cliente_legal_note || "").trim(),
        carta_proveedor_intro: String(draft.carta_proveedor_intro || "").trim(),
        carta_proveedor_footer: String(draft.carta_proveedor_footer || "").trim(),
      };
      // Vaciar si coincide con default → vuelve al texto estándar del sistema.
      LETTER_FIELDS.forEach(({ key }) => {
        const cur = String(payload[key] || "").trim();
        const def = String(defaults[key] || "").trim();
        if (cur && def && cur === def) payload[key] = "";
      });
      const data = await saveMaterialidadConfig(clientId, payload);
      const cfg = data.config || payload;
      setBaseConfig(cfg);
      const next = {};
      LETTER_FIELDS.forEach(({ key }) => {
        next[key] = effectiveLetterValue(cfg, defaults, key);
      });
      setDraft(next);
      toast.success("Redacción de cartas guardada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar la redacción.");
    } finally {
      setSaving(false);
    }
  };

  if (!clientId) {
    return (
      <p className="text-[11px] text-slate-500 m-0 px-2 py-2">
        Seleccione una empresa para editar la redacción de cartas.
      </p>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-slate-600 px-2 py-3">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        Cargando redacción…
      </div>
    );
  }

  return (
    <div
      className="space-y-3 rounded-sm border border-slate-100 bg-slate-50/40 p-3"
      data-testid={`materialidad-cartas-redaccion-${clientId}`}
    >
      <div className="space-y-1">
        <p className="text-xs font-medium text-slate-800 m-0">
          Redacción de cartas — {clientName || "empresa"}
        </p>
        <p className="text-[11px] text-slate-600 m-0">
          {note ||
            "Solo se edita el texto general. Los datos del despacho, cliente y proveedores se insertan solos al generar el PDF."}
        </p>
        <div className="flex flex-wrap gap-1 pt-1">
          {placeholderChips.map((chip) => (
            <span
              key={chip}
              className="inline-flex items-center rounded border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[10px] text-slate-600"
            >
              {chip}
            </span>
          ))}
          <span className="text-[10px] text-slate-500 self-center ml-1">
            (variables fijas — no editables)
          </span>
        </div>
      </div>

      <section className="space-y-2" aria-label="Carta despacho a cliente">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 m-0">
          1. Carta resumen (despacho → cliente)
        </h4>
        {LETTER_FIELDS.filter((f) => f.letter === "cliente").map((field) => (
          <div key={field.key} className="space-y-1">
            <Label htmlFor={`${clientId}-${field.key}`} className="text-[11px] text-slate-700">
              {field.label}
            </Label>
            <p className="text-[10px] text-slate-500 m-0">{field.hint}</p>
            <Textarea
              id={`${clientId}-${field.key}`}
              value={draft[field.key] || ""}
              onChange={(e) => patchDraft(field.key, e.target.value)}
              rows={field.key.includes("legal") ? 5 : 4}
              className="text-xs min-h-[72px] bg-white"
              data-testid={`materialidad-carta-field-${field.key}`}
            />
          </div>
        ))}
      </section>

      <section className="space-y-2" aria-label="Carta cliente a proveedor">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 m-0">
          2. Carta al proveedor (cliente → proveedor)
        </h4>
        {LETTER_FIELDS.filter((f) => f.letter === "proveedor").map((field) => (
          <div key={field.key} className="space-y-1">
            <Label htmlFor={`${clientId}-${field.key}`} className="text-[11px] text-slate-700">
              {field.label}
            </Label>
            <p className="text-[10px] text-slate-500 m-0">{field.hint}</p>
            <Textarea
              id={`${clientId}-${field.key}`}
              value={draft[field.key] || ""}
              onChange={(e) => patchDraft(field.key, e.target.value)}
              rows={4}
              className="text-xs min-h-[72px] bg-white"
              data-testid={`materialidad-carta-field-${field.key}`}
            />
          </div>
        ))}
      </section>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          className="h-8 text-xs"
          onClick={handleSave}
          disabled={saving}
          data-testid={`materialidad-cartas-guardar-${clientId}`}
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
          Guardar redacción
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 text-xs"
          onClick={restoreDefaults}
          disabled={saving}
          data-testid={`materialidad-cartas-restaurar-${clientId}`}
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1" />
          Restaurar plantilla
        </Button>
      </div>
    </div>
  );
}
